import React, { useState, useEffect } from 'react';

/**
 * OfflineIndicator - Normalizes offline/online state
 *
 * Shows current connection status subtly.
 * Offline is not an error - it's an expected, normal state.
 *
 * Design principles:
 * - Subtle, not alarming
 * - Offline is neutral, not negative
 * - Shows sync status when it matters
 * - Doesn't interrupt learning flow
 */
function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowIndicator(true);
      // Hide after 3 seconds when back online
      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Show initially if offline
    if (!navigator.onLine) {
      setShowIndicator(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!showIndicator) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      <div
        className={`px-4 py-2 rounded-lg shadow-lg border transition-all ${
          isOnline
            ? 'bg-green-900/90 border-green-600 text-green-100'
            : 'bg-slate-800/90 border-slate-600 text-slate-200'
        }`}
      >
        <div className="flex items-center gap-2">
          {/* Icon - subtle indicator */}
          <span className="text-sm">
            {isOnline ? '📶' : '📵'}
          </span>

          {/* Status text - calm, informative */}
          <span className="text-sm font-medium">
            {isOnline ? 'Back online' : 'Offline mode'}
          </span>
        </div>

        {/* Reassurance message - only when offline */}
        {!isOnline && (
          <p className="text-xs text-slate-400 mt-1">
            Your progress saves locally. Continue learning.
          </p>
        )}
      </div>
    </div>
  );
}

export default OfflineIndicator;
